/**
 * StorageService
 * Centralized storage abstraction layer — prevents direct localStorage access.
 * All code should use this service instead of calling localStorage directly.
 */

const STORAGE_KEYS = {
    AUTH_TOKEN: 'authToken',
    USER_DATA: 'userData',
    LOCATION_CACHE: 'locationCache',
    PREFERENCES: 'userPreferences',
};

export const storageService = {
    // ─── Auth Token ──────────────────────────────────────────────────────────────
    getAuthToken: () => {
        try {
            return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || null;
        } catch (err) {
            console.error('StorageService: Failed to read auth token', err);
            return null;
        }
    },

    setAuthToken: (token) => {
        try {
            if (token) {
                localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
            } else {
                localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
            }
        } catch (err) {
            console.error('StorageService: Failed to set auth token', err);
        }
    },

    removeAuthToken: () => {
        try {
            localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        } catch (err) {
            console.error('StorageService: Failed to remove auth token', err);
        }
    },

    // ─── User Data ────────────────────────────────────────────────────────────────
    getUserData: () => {
        try {
            const data = localStorage.getItem(STORAGE_KEYS.USER_DATA);
            return data ? JSON.parse(data) : null;
        } catch (err) {
            console.error('StorageService: Failed to parse user data', err);
            return null;
        }
    },

    setUserData: (userData) => {
        try {
            if (userData) {
                localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
            } else {
                localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            }
        } catch (err) {
            console.error('StorageService: Failed to set user data', err);
        }
    },

    removeUserData: () => {
        try {
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        } catch (err) {
            console.error('StorageService: Failed to remove user data', err);
        }
    },

    // ─── Convenience ─────────────────────────────────────────────────────────────
    /** Clears both auth token and user data (logout) */
    clearAuth: () => {
        try {
            localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        } catch (err) {
            console.error('StorageService: Failed to clear auth data', err);
        }
    },

    hasAuthToken: () => {
        return !!storageService.getAuthToken();
    },

    isAuthenticated: () => {
        return !!(storageService.getAuthToken() && storageService.getUserData());
    },

    // ─── Preferences ─────────────────────────────────────────────────────────────
    getPreferences: () => {
        try {
            const data = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
            return data ? JSON.parse(data) : {};
        } catch (err) {
            return {};
        }
    },

    setPreference: (key, value) => {
        try {
            const prefs = storageService.getPreferences();
            prefs[key] = value;
            localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(prefs));
        } catch (err) {
            console.error('StorageService: Failed to set preference', err);
        }
    },
};
